import { ShoppingCart, User, LogOut, Menu, X, ChevronDown, Settings, Code, Shield, History, Wallet, HelpCircle } from 'lucide-react';
import logoUrl from '@/assets/logo.png';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SteamIcon } from './icons/SteamIcon';
import type { Profile } from '@/lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface HeaderProps {
  currentPage: 'market' | 'profile';
  onPageChange: (page: 'market' | 'profile') => void;
  isLoggedIn: boolean;
  onLogin: () => void;
  onLogout: () => void;
  user?: Profile | null;
}

export function Header({ 
  currentPage, 
  onPageChange, 
  isLoggedIn, 
  onLogin, 
  onLogout,
  user
}: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isB2BApiPage = location.pathname === '/b2b-api';

  return (
    <header className="sticky top-0 z-50 w-full border-b border-[#1a2332] bg-[#0d1117]/95 backdrop-blur supports-[backdrop-filter]:bg-[#0d1117]/80">
      <div className="w-full px-4 sm:px-6 lg:px-8 max-w-[1920px] mx-auto flex h-16 items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => onPageChange('market')}
        >
          <img
            src={logoUrl}
            alt="Xpanda"
            className="w-12 h-12 object-contain flex-shrink-0"
          />
          <div className="hidden sm:block">
            <span className="text-white font-bold text-xl">MARKET</span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          <Button
            variant="ghost"
            onClick={() => {
              navigate('/');
              onPageChange('market');
            }}
            className={`text-sm font-medium transition-colors ${
              currentPage === 'market'
                ? 'text-[#ff6b00] bg-[#ff6b00]/10'
                : 'text-[#8b949e] hover:text-white hover:bg-[#1a2332]'
            }`}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Маркетплейс
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/b2b-api')}
            className={`text-sm font-medium transition-colors ${
              isB2BApiPage
                ? 'text-[#ff6b00] bg-[#ff6b00]/10'
                : 'text-[#8b949e] hover:text-white hover:bg-[#1a2332]'
            }`}
          >
            <Code className="w-4 h-4 mr-2" />
            B2B API
          </Button>
          {isLoggedIn && (
            <Button
              variant="ghost"
              onClick={() => {
                navigate('/profile');
                onPageChange('profile');
              }}
              className={`text-sm font-medium transition-colors ${
                currentPage === 'profile'
                  ? 'text-[#ff6b00] bg-[#ff6b00]/10'
                  : 'text-[#8b949e] hover:text-white hover:bg-[#1a2332]'
              }`}
            >
              <User className="w-4 h-4 mr-2" />
              Профиль
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => navigate('/faq')}
            className={`text-sm font-medium transition-colors ${
              location.pathname === '/faq'
                ? 'text-[#ff6b00] bg-[#ff6b00]/10'
                : 'text-[#8b949e] hover:text-white hover:bg-[#1a2332]'
            }`}
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            FAQ
          </Button>
          {isLoggedIn && user?.is_admin && (
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/admin'
                  ? 'text-[#ff6b00] bg-[#ff6b00]/10'
                  : 'text-[#8b949e] hover:text-white hover:bg-[#1a2332]'
              }`}
            >
              <Shield className="w-4 h-4 mr-2" />
              Админ
            </Button>
          )}
        </nav>

        {/* Right Section */}
        <div className="flex items-center gap-2">
          {isLoggedIn ? (
            <>
              {/* User Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 hover:bg-[#1a2332]"
                  >
                    {user?.steam_avatar ? (
                      <img 
                        src={user.steam_avatar} 
                        alt={user.steam_name}
                        className="w-8 h-8 rounded-full border border-[#30363d]"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#1a2332] flex items-center justify-center">
                        <User className="w-4 h-4 text-[#8b949e]" />
                      </div>
                    )}
                    <span className="hidden sm:block text-white max-w-[120px] truncate">
                      {user?.steam_name || 'Пользователь'}
                    </span>
                    {user?.balance !== null && user?.balance !== undefined && (
                      <span className="hidden sm:block text-xs font-semibold text-[#ff6b00] bg-[#ff6b00]/10 border border-[#ff6b00]/20 px-1.5 py-0.5 rounded flex-shrink-0">
                        {Math.floor(user.balance).toLocaleString('ru-RU')} ₽
                      </span>
                    )}
                    <ChevronDown className="w-4 h-4 text-[#8b949e]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#161b22] border-[#30363d]">
                  <div className="px-3 py-2">
                    <p className="text-sm text-[#8b949e]">Вошли как</p>
                    <p className="text-white font-medium truncate">{user?.steam_name}</p>
                    {user?.balance !== null && user?.balance !== undefined && (
                      <p className="text-[#ff6b00] font-semibold text-sm mt-0.5">
                        {Math.floor(user.balance).toLocaleString('ru-RU')} ₽
                      </p>
                    )}
                  </div>
                  <DropdownMenuSeparator className="bg-[#30363d]" />
                  {/* Навигационные ссылки */}
                  <DropdownMenuItem
                    onClick={() => { navigate('/'); onPageChange('market'); }}
                    className="text-[#8b949e] hover:text-white hover:bg-[#1a2332] cursor-pointer"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Маркетплейс
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/b2b-api')}
                    className="text-[#8b949e] hover:text-white hover:bg-[#1a2332] cursor-pointer"
                  >
                    <Code className="w-4 h-4 mr-2" />
                    B2B API
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => { navigate('/profile?tab=history'); onPageChange('profile'); }}
                    className="text-[#8b949e] hover:text-white hover:bg-[#1a2332] cursor-pointer"
                  >
                    <History className="w-4 h-4 mr-2" />
                    Мои заказы
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => { navigate('/profile?action=deposit'); onPageChange('profile'); }}
                    className="text-[#ff6b00] hover:text-white hover:bg-[#ff6b00]/20 cursor-pointer font-medium"
                  >
                    <Wallet className="w-4 h-4 mr-2" />
                    Пополнить
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#30363d]" />
                  <DropdownMenuItem
                    onClick={() => {
                      navigate('/profile');
                      onPageChange('profile');
                    }}
                    className="text-white hover:bg-[#1a2332] cursor-pointer"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Настройки
                  </DropdownMenuItem>
                  {user?.is_admin && (
                    <>
                      <DropdownMenuSeparator className="bg-[#30363d]" />
                      <DropdownMenuItem
                        onClick={() => navigate('/admin')}
                        className="text-[#ff6b00] hover:bg-[#ff6b00]/10 cursor-pointer"
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Панель админа
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-[#30363d]" />
                  <DropdownMenuItem 
                    onClick={onLogout}
                    className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Выйти
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button
              onClick={onLogin}
              className="bg-gradient-to-r from-[#1b2838] to-[#2a475e] hover:from-[#2a475e] hover:to-[#1b2838] text-white border border-[#66c0f4]/30"
            >
              <SteamIcon className="w-4 h-4 mr-2" />
              Войти
            </Button>
          )}

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-[#8b949e]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-[#1a2332] bg-[#0d1117]">
          <div className="w-full px-4 py-4 space-y-2">
            <Button
              variant="ghost"
              onClick={() => {
                navigate('/');
                onPageChange('market');
                setMobileMenuOpen(false);
              }}
              className={`w-full justify-start ${
                currentPage === 'market'
                  ? 'text-[#ff6b00] bg-[#ff6b00]/10'
                  : 'text-[#8b949e]'
              }`}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Маркетплейс
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                navigate('/b2b-api');
                setMobileMenuOpen(false);
              }}
              className={`w-full justify-start ${
                isB2BApiPage
                  ? 'text-[#ff6b00] bg-[#ff6b00]/10'
                  : 'text-[#8b949e]'
              }`}
            >
              <Code className="w-4 h-4 mr-2" />
              B2B API
            </Button>
            {isLoggedIn && (
              <Button
                variant="ghost"
                onClick={() => {
                  navigate('/profile');
                  onPageChange('profile');
                  setMobileMenuOpen(false);
                }}
                className={`w-full justify-start ${
                  currentPage === 'profile'
                    ? 'text-[#ff6b00] bg-[#ff6b00]/10'
                    : 'text-[#8b949e]'
                }`}
              >
                <User className="w-4 h-4 mr-2" />
                Профиль
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => {
                navigate('/faq');
                setMobileMenuOpen(false);
              }}
              className={`w-full justify-start ${
                location.pathname === '/faq'
                  ? 'text-[#ff6b00] bg-[#ff6b00]/10'
                  : 'text-[#8b949e]'
              }`}
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              FAQ
            </Button>
            {isLoggedIn && user?.is_admin && (
              <Button
                variant="ghost"
                onClick={() => {
                  navigate('/admin');
                  setMobileMenuOpen(false);
                }}
                className={`w-full justify-start ${
                  location.pathname === '/admin'
                    ? 'text-[#ff6b00] bg-[#ff6b00]/10'
                    : 'text-[#8b949e]'
                }`}
              >
                <Shield className="w-4 h-4 mr-2" />
                Панель админа
              </Button>
            )}
            {!isLoggedIn && (
              <Button
                onClick={() => {
                  onLogin();
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-gradient-to-r from-[#1b2838] to-[#2a475e] text-white"
              >
                <SteamIcon className="w-4 h-4 mr-2" />
                Войти через Steam
              </Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
